import { SafeMarkdown } from '../../components/SafeMarkdown';
import { Badge } from '../../components/Badge';
import { useFormat } from '../../i18n/format';
import { useT } from '../../i18n/provider';
import type { Message, MessageDecision } from '../../api/communication';

/**
 * One message — or one attempt that never became a message.
 *
 * Shared by the viewer of `06 §7` and the «Conversaciones» tab of a task
 * (`13 §3.7`), which show the same rows from two sides; one drawing keeps the
 * two from disagreeing about what a blocked attempt looks like.
 *
 * The blocked branch is visually loud on purpose. `06 §7` draws it with a ⛔ and
 * the policy underneath, because a thread that showed refusals in the same grey
 * as everything else would hide the only rows a manager has to act on.
 */
export function MessageRow({ message }: { message: Message }) {
  const t = useT();
  const format = useFormat();

  if (message.blocked) {
    return <BlockedRow message={message} decision={message.decision} />;
  }

  return (
    <article className="rounded-md border border-line bg-raised p-3.5 transition-colors hover:border-line-strong/40">
      <header className="flex flex-wrap items-baseline gap-x-2 text-xs text-ink-faint">
        <span className="font-mono font-semibold text-ink">{message.from.slug}</span>
        <span>{message.from.job_title}</span>
        <span>·</span>
        <span>{format.dateTime(message.created_at)}</span>
        {message.type === 'escalation' ? (
          <Badge tone="warning" size="sm">
            {t('conversations.escalation')}
          </Badge>
        ) : null}
        {message.type === 'delegation' ? (
          <Badge tone="info" size="sm">
            {t('conversations.delegation')}
          </Badge>
        ) : null}
        {message.priority === 'urgent' ? (
          <Badge tone="danger" size="sm">
            {t('conversations.urgent')}
          </Badge>
        ) : null}
        {message.redacted ? (
          <Badge tone="neutral" size="sm" title={t('conversations.redactedTitle')}>
            {t('conversations.redacted')}
          </Badge>
        ) : null}
      </header>
      {message.subject === null ? null : <p className="mt-1.5 text-sm font-semibold text-ink">{message.subject}</p>}
      <div className="mt-2 text-sm text-ink">
        <SafeMarkdown>{message.body}</SafeMarkdown>
      </div>
    </article>
  );
}

function BlockedRow({ message, decision }: { message: Message; decision: MessageDecision | null }) {
  const t = useT();
  const format = useFormat();

  return (
    <article className="rounded-md border border-danger-line bg-danger-pale p-3.5">
      <header className="flex flex-wrap items-baseline gap-x-2 text-xs">
        <span aria-hidden>⛔</span>
        <span className="font-mono font-semibold text-ink">{message.from.slug}</span>
        <span className="text-ink-soft">→ {decision?.target_descriptor ?? t('conversations.recipients')}</span>
        <span className="text-ink-soft">· {format.dateTime(message.created_at)}</span>
        <span className="font-bold text-danger">{t('conversations.blocked')}</span>
      </header>
      <div className="mt-2 italic">
        <SafeMarkdown>{message.body}</SafeMarkdown>
      </div>
      {decision === null ? null : (
        <p className="mt-2 text-xs text-ink-soft">
          {/* `reason_code` es el identificador estable de la política que
              paró el mensaje: se pinta tal cual. */}
          <span className="font-mono">{decision.reason_code}</span> · {decision.reason_detail}
        </p>
      )}
    </article>
  );
}
